import { Space } from "./roles";

// Catalogue des avatars par défaut proposés à l'utilisateur (thème élevage / poulailler).
//
// Jeu d'ESSAI volontairement simple (2026-07) : chaque avatar est un emoji sur une pastille
// colorée — zéro fichier image, zéro dépendance de licence. Ce qui compte et reste STABLE,
// c'est l'`id` : il est stocké tel quel dans Account.avatar (colonne TEXT). Quand on remplacera
// ces placeholders par de vraies icônes plus tard, il suffira de changer le rendu (composant
// Avatar) sans toucher aux données ni aux ids déjà enregistrés.
//
// `space` : chaque avatar n'est proposé qu'aux utilisateurs de cet espace (éleveur / installateur /
// groupement / admin). Les assistants partagent l'espace de leur type principal (voir roles.ts),
// donc voient le même jeu. Répartition arbitraire pour le moment (le thème emoji est limité) :
// changer le `space` d'une ligne suffit à re-répartir.
export interface AvatarPreset {
  id: string;
  label: string;
  emoji: string;
  bg: string; // couleur de fond de la pastille
  space: Space; // espace auquel cet avatar est proposé
}

export const AVATAR_CATALOG: AvatarPreset[] = [
  // — Éleveur : volaille & bétail —
  { id: "poule", label: "Poule", emoji: "🐔", bg: "#FDE7C7", space: "breeder" },
  { id: "coq", label: "Coq", emoji: "🐓", bg: "#FAD4D4", space: "breeder" },
  { id: "poussin", label: "Poussin", emoji: "🐣", bg: "#FFF3C4", space: "breeder" },
  { id: "poussin-jaune", label: "Poussin jaune", emoji: "🐤", bg: "#FEF08A", space: "breeder" },
  { id: "oeuf", label: "Œuf", emoji: "🥚", bg: "#F1F5F9", space: "breeder" },
  { id: "canard", label: "Canard", emoji: "🦆", bg: "#D6E9F5", space: "breeder" },
  { id: "dinde", label: "Dinde", emoji: "🦃", bg: "#E9D8C4", space: "breeder" },
  { id: "vache", label: "Vache", emoji: "🐄", bg: "#E7EDE3", space: "breeder" },
  { id: "cochon", label: "Cochon", emoji: "🐷", bg: "#FAD4D4", space: "breeder" },
  // — Installateur : terrain / matériel / cultures —
  { id: "tracteur", label: "Tracteur", emoji: "🚜", bg: "#D8ECD0", space: "installer" },
  { id: "ferme", label: "Ferme", emoji: "🏡", bg: "#E3E8F0", space: "installer" },
  { id: "ble", label: "Blé", emoji: "🌾", bg: "#F3E8C0", space: "installer" },
  { id: "mais", label: "Maïs", emoji: "🌽", bg: "#FCEFB4", space: "installer" },
  { id: "arbre", label: "Arbre", emoji: "🌳", bg: "#D8ECD0", space: "installer" },
  { id: "tournesol", label: "Tournesol", emoji: "🌻", bg: "#FEF08A", space: "installer" },
  { id: "carotte", label: "Carotte", emoji: "🥕", bg: "#FDE7C7", space: "installer" },
  { id: "tomate", label: "Tomate", emoji: "🍅", bg: "#FAD4D4", space: "installer" },
  { id: "salade", label: "Salade", emoji: "🥬", bg: "#E7EDE3", space: "installer" },
  // — Groupement : animaux variés —
  { id: "mouton", label: "Mouton", emoji: "🐑", bg: "#F1F5F9", space: "group" },
  { id: "chevre", label: "Chèvre", emoji: "🐐", bg: "#E7EDE3", space: "group" },
  { id: "cheval", label: "Cheval", emoji: "🐴", bg: "#E9D8C4", space: "group" },
  { id: "lapin", label: "Lapin", emoji: "🐰", bg: "#F1F5F9", space: "group" },
  { id: "chien", label: "Chien", emoji: "🐕", bg: "#FDE7C7", space: "group" },
  { id: "chat", label: "Chat", emoji: "🐈", bg: "#E3E8F0", space: "group" },
  { id: "cerf", label: "Cerf", emoji: "🦌", bg: "#E9D8C4", space: "group" },
  { id: "hibou", label: "Hibou", emoji: "🦉", bg: "#E9D8C4", space: "group" },
  { id: "cygne", label: "Cygne", emoji: "🦢", bg: "#F1F5F9", space: "group" },
  // — Admin : divers —
  { id: "poussin-face", label: "Poussin (face)", emoji: "🐥", bg: "#FFF3C4", space: "admin" },
  { id: "oiseau", label: "Oiseau", emoji: "🐦", bg: "#D6E9F5", space: "admin" },
  { id: "abeille", label: "Abeille", emoji: "🐝", bg: "#FEF08A", space: "admin" },
  { id: "souris", label: "Souris", emoji: "🐭", bg: "#F1F5F9", space: "admin" },
  { id: "coccinelle", label: "Coccinelle", emoji: "🐞", bg: "#FAD4D4", space: "admin" },
  { id: "escargot", label: "Escargot", emoji: "🐌", bg: "#E7EDE3", space: "admin" },
  { id: "pomme", label: "Pomme", emoji: "🍎", bg: "#FAD4D4", space: "admin" },
  { id: "raisin", label: "Raisin", emoji: "🍇", bg: "#E6DAF0", space: "admin" },
  { id: "panier", label: "Panier", emoji: "🧺", bg: "#F3E8C0", space: "admin" },
];

// avatarsForSpace : avatars proposés à un espace donné (le picker n'affiche que ceux-là).
export const avatarsForSpace = (space: Space): AvatarPreset[] =>
  AVATAR_CATALOG.filter((a) => a.space === space);

// getAvatarPreset retourne l'entrée du catalogue pour un id donné, ou undefined si la valeur
// stockée n'est pas un id connu (ancien avatar base64/URL, ou vide). Recherche dans tout le
// catalogue (indépendant de l'espace) : un avatar déjà enregistré doit toujours s'afficher.
export const getAvatarPreset = (id: string | null | undefined): AvatarPreset | undefined =>
  id ? AVATAR_CATALOG.find((a) => a.id === id) : undefined;
